import { useState, useRef, useCallback, useEffect } from 'react';
import { removeBackground, blobToDataUrl } from '../lib/backgroundRemoval.js';
import { SignaturePad } from './SignaturePad.js';
import { useAuth } from '../lib/auth.js';

type Mode = 'draw' | 'upload';

interface ImageCaptureProps {
  label: string;          // e.g. 'Signature' or 'Stamp'
  /** Existing image — may be a data URL or an auth-protected API URL. */
  value?: string;
  onChange: (dataUrl: string) => void;
  defaultMode?: Mode;
  /** Hide the Draw tab — only show the Upload tab (e.g. for stamps). */
  hideDraw?: boolean;
}

/**
 * Combined draw-or-upload component for signatures and stamps.
 *
 * - Draw tab: canvas signature pad.
 * - Upload tab: file picker + canvas-based black-and-white conversion
 *   (dark ink stays, light background becomes transparent).
 *
 * When given an API URL (e.g. on edit), fetches it with the auth token and
 * converts it to a data URL so the SignaturePad can display it without
 * making its own unauthenticated request.
 *
 * Emits a transparent PNG data URL that the PersonnelPage sends to the API.
 */
export function ImageCapture({ label, value, onChange, defaultMode = 'upload', hideDraw = false }: ImageCaptureProps) {
  const { user } = useAuth();
  const [mode, setMode] = useState<Mode>(hideDraw ? 'upload' : defaultMode);
  // dataUrl is always a data URL — either passed directly, fetched from an
  // API URL, produced by drawing, or produced by uploading.
  const [dataUrl, setDataUrl] = useState<string | undefined>(
    value?.startsWith('data:image/') ? value : undefined,
  );
  // Incremented to force the SignaturePad to remount with a fresh canvas when
  // the user clears — avoids stale canvas content being re-emitted.
  const [clearKey, setClearKey] = useState(0);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Tracks whether an in-flight API-image fetch should be discarded (set when
  // the user clears before the fetch resolves — clearing doesn't change `value`,
  // so the effect's `cancelled` flag alone can't catch it).
  const fetchDiscardedRef = useRef(false);

  // Tracks the last `value` we actually fetched, so we can skip effect
  // re-runs triggered by `user` changing identity (Firebase re-emits auth
  // state) rather than by the image itself changing. Re-fetching on a mere
  // `user` re-render would undo an explicit clear — the guard above would be
  // reset to false and a fresh fetch would repopulate dataUrl.
  const prevValueRef = useRef<string | undefined>(undefined);

  // If value is an API URL (not a data URL), fetch it with auth and convert to
  // a data URL so the SignaturePad can display it without re-fetching.
  useEffect(() => {
    if(!value) return;
    // Only (re)fetch when the image itself changes. A re-render caused by
    // `user` changing identity must not re-fetch, or it would undo a clear.
    const hasChanged = prevValueRef.current !== value;
    prevValueRef.current = value;
    if (!hasChanged) return;

    fetchDiscardedRef.current = false;
    if (value.startsWith('data:image/')) {
      setDataUrl(value);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
     
        const token = await user?.getIdToken();
        const res = await fetch(value, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok || fetchDiscardedRef.current) return;
        const blob = await res.blob();
        const url = await blobToDataUrl(blob);
        if (!cancelled && !fetchDiscardedRef.current) setDataUrl(url);
      } catch { /* keep empty — SignaturePad will show placeholder */ }
    })();
    return () => { cancelled = true; };
  }, [value, user]);

  // When the user draws, the SignaturePad calls onChange directly.
  // An empty string means the user cleared the canvas — clear everything.
  const handleDrawChange = useCallback((url: string) => {
    if (!url) {
      setDataUrl(undefined);
      setClearKey((k) => k + 1);
      onChange('');
      return;
    }
    setDataUrl(url);
    onChange(url);
  }, [onChange]);

  const handleFile = useCallback(async (file: File) => {
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file (PNG, JPG, etc.)');
      return;
    }
    setUploading(true);
    setError('');
    try {
      const processedBlob = await removeBackground(file);
      const url = await blobToDataUrl(processedBlob);
      setDataUrl(url);
      onChange(url);
    } catch {
      setError('Failed to process image. Try a different format.');
    } finally {
      setUploading(false);
    }
  }, [onChange]);

  const onFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
    // Reset so the same file can be re-selected.
    e.target.value = '';
  }, [handleFile]);

  const onDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  }, [handleFile]);

  const onDragOver = useCallback((e: React.DragEvent) => { e.preventDefault(); }, []);

  const clear = useCallback(() => {
    fetchDiscardedRef.current = true;
    setDataUrl(undefined);
    setClearKey((k) => k + 1);
    onChange('');
    setError('');
  }, [onChange]);

  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label className="label mb-0">{label}</label>
        {!hideDraw && (
          <div className="flex gap-1">
            <TabButton active={mode === 'upload'} onClick={() => setMode('upload')}>Upload</TabButton>
            <TabButton active={mode === 'draw'} onClick={() => setMode('draw')}>Draw</TabButton>
          </div>
        )}
      </div>

      {mode === 'draw' ? (
        <SignaturePad dataUrl={dataUrl} clearKey={clearKey} onChange={handleDrawChange} />
      ) : (
        <div
          className="rounded-lg border-2 border-dashed border-gray-300 p-6 text-center cursor-pointer hover:border-brand-400 transition-colors"
          onDrop={onDrop}
          onDragOver={onDragOver}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={onFileSelect}
            disabled={uploading}
          />
          {uploading ? (
            <div className="flex items-center justify-center gap-2 py-6">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-brand-600" />
              <span className="text-sm text-gray-600">Processing…</span>
            </div>
          ) : dataUrl ? (
            <div className="space-y-3">
              <ImagePreview src={dataUrl} label={label} />
              <p className="text-xs text-gray-400">Click or drop a new image to replace</p>
            </div>
          ) : (
            <div className="space-y-2">
              <svg className="w-8 h-8 mx-auto text-gray-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4m4-5l5-5 5 5m-5-5v12" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <p className="text-sm text-gray-600 font-medium">Drop an image or click to upload</p>
              <p className="text-xs text-gray-400">Background will be removed automatically</p>
            </div>
          )}
        </div>
      )}

      {error && <p className="text-xs text-red-600 mt-1">{error}</p>}
      
      {dataUrl && (
        <div className="flex justify-end mt-2">
          <button type="button" onClick={clear} className="btn btn-ghost text-xs py-1 px-2 text-gray-500">
            <svg className="w-3.5 h-3.5 mr-1" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Remove
          </button>
        </div>
      )}
    </div>
  );
}

// ── Sub-components ─────────────────────────

function TabButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`text-xs px-3 py-1 rounded-full border transition-colors ${
        active
          ? 'bg-brand-600 text-white border-brand-600'
          : 'bg-white text-gray-600 border-gray-300 hover:border-gray-400'
      }`}
    >
      {children}
    </button>
  );
}

function ImagePreview({ src, label }: { src: string; label: string }) {
  return (
    <div className="flex flex-col items-center gap-2">
      <div className="relative rounded border border-gray-200 p-2" style={{
        backgroundImage: 'linear-gradient(45deg,#eee 25%,transparent 25%),linear-gradient(-45deg,#eee 25%,transparent 25%),linear-gradient(45deg,transparent 25%,#eee 25%),linear-gradient(-45deg,transparent 25%,#eee 25%)',
        backgroundSize: '8px 8px',
        backgroundPosition: '0 0,0 4px,4px -4px,-4px 0',
      }}>
        <img src={src} alt={`${label} preview`} className="max-h-32 object-contain" />
      </div>
      <span className="text-xs text-gray-400">Transparent areas shown as checkerboard</span>
    </div>
  );
}
